import { forwardRef, useMemo, useState } from "react";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { players } from "@/helpers/formatters";
import { useIsBreakPoint } from "@/hooks/use-is-break-point";
import { cn } from "@/lib/utils";

import type { TagType } from "../../types";
import CommunityModal from "./community-modal";
import {
  CommunityIcon,
  DifficultyIcon,
  DurationIcon,
  PlayersIcon,
  ScaryIcon,
} from "./icons";

function CommunityInfo() {
  return (
    <>
      <p className="text-14/[1.71em] text-white">
        <span className="bg-linear-92 from-4% from-cyan to-41% to-cerulean bg-clip-text text-transparent">
          Community tags
        </span>{" "}
        show what Morty users think about an attraction.
      </p>
      <p className="mt-6 text-14/[1.71em] text-white">
        <span className="text-heather">Gray tags</span> represent official
        information provided by the attraction.
      </p>
    </>
  );
}

interface PropertyProps extends React.ComponentProps<"li"> {
  children?: React.ReactNode;
  className?: string;
  icon: React.ReactNode;
  title: string;
}

const Property = forwardRef<HTMLLIElement, PropertyProps>(function Property(
  { children, className, icon, title, ...props },
  ref
) {
  return (
    <li
      className={cn(
        "flex h-[45px] grow-1 items-center justify-center rounded-[5px] bg-white/10 px-2.5 md:grow-0",
        className
      )}
      ref={ref}
      {...props}
    >
      {icon}
      <strong className="ml-1.5 font-medium text-14 md:font-normal">
        {title}
      </strong>
      {children}
    </li>
  );
});

export interface HighlightPropertiesProps {
  className?: string;
  difficulty?: string;
  minutes: number;
  playersMax: number;
  playersMin: number;
  tags?: TagType[];
}

export default function HighlightProperties({
  className,
  difficulty,
  minutes,
  playersMax,
  playersMin,
  tags = [],
}: HighlightPropertiesProps) {
  const [isCommunityModalOpen, setIsCommunityModalOpen] = useState(false);
  const isDesktop = useIsBreakPoint("md");

  const properties = useMemo(() => {
    const out: React.ReactNode[] = [];
    const playersString = players(playersMin, playersMax);
    const scaryTag = tags.find(
      (tag) => tag.slug === "scariness" || tag.parent?.slug === "scariness"
    );

    if (playersString !== "") {
      out.push(
        <Property icon={<PlayersIcon />} key="players" title={playersString} />
      );
    }
    if (minutes > 0) {
      out.push(
        <Property
          icon={<DurationIcon />}
          key="minutes"
          title={`${minutes} mins`}
        />
      );
    }
    if (!!difficulty && difficulty !== "Unknown") {
      out.push(
        <Property
          icon={<DifficultyIcon />}
          key="difficulty"
          title={difficulty}
        />
      );
    }
    if (scaryTag) {
      out.push(
        <HoverCard
          key="scariness"
          onOpenChange={setIsCommunityModalOpen}
          open={isCommunityModalOpen}
        >
          <HoverCardTrigger asChild>
            <Property
              className={cn(
                "grow-0",
                scaryTag.isCommunity &&
                  "relative cursor-pointer bg-linear-92 from-4% from-cyan to-41% to-cerulean"
              )}
              icon={
                <ScaryIcon
                  className={
                    scaryTag.isCommunity ? "text-white" : "text-dark-raspberry"
                  }
                />
              }
              onClick={() => setIsCommunityModalOpen(true)}
              title={scaryTag.name}
            >
              {scaryTag.isCommunity && (
                <div className="absolute -top-1.5 -right-[5px] flex size-4.5 items-center justify-center rounded-full border border-black-russian-dark bg-white">
                  <CommunityIcon />
                </div>
              )}
            </Property>
          </HoverCardTrigger>
          {isDesktop && (
            <HoverCardContent
              align="start"
              className="w-80 rounded-[20px] bg-twilight-indigo shadow-0-0-4 shadow-deep-koamaru"
              side="bottom"
            >
              <h5 className="border-white/10 border-b py-5 text-center font-semibold text-16 text-white">
                What’s this?
              </h5>
              <div className="p-5">
                <CommunityInfo />
              </div>
            </HoverCardContent>
          )}
        </HoverCard>
      );
    }

    return out;
  }, [
    difficulty,
    minutes,
    playersMin,
    playersMax,
    tags,
    isDesktop,
    isCommunityModalOpen,
  ]);

  if (!properties.length) {
    return null;
  }

  return (
    <>
      <ul className={cn("flex flex-wrap gap-2.5", className)}>{properties}</ul>
      {!isDesktop && (
        <CommunityModal
          onOpenChange={setIsCommunityModalOpen}
          open={isCommunityModalOpen}
        >
          <CommunityInfo />
        </CommunityModal>
      )}
    </>
  );
}
